import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import SolicitacaoService from "../../services/resource/solicitacaoService";
import AnexoSolicitacaoService from "../../services/resource/anexoSolicitacaoService";
import { processoInfo } from "../../utils/solicitacoes/processoRegistry";

const solicitacaoService = new SolicitacaoService();
const anexoService = new AnexoSolicitacaoService();

// Hook central da tela de Detalhe: solicitação base (com acoesDisponiveis), timeline, anexos e,
// quando o processo tiver (Despesa/Compras), o detalhe específico do processo.
export function useSolicitacaoDetalhe(id) {
    const [solicitacao, setSolicitacao] = useState(null);
    const [detalheProcesso, setDetalheProcesso] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [executores, setExecutores] = useState([]);
    const [anexos, setAnexos] = useState([]);
    const [loading, setLoading] = useState(true);
    // true assim que a primeira carga (solicitação + timeline + anexos + detalhe + executores)
    // termina, sucesso ou erro — diferente de `loading` (que também fica true em recargas
    // disparadas por ações/comentários/anexos), serve pra decidir quando trocar o skeleton da
    // página inteira pelo conteúdo real, sem re-exibir esse skeleton nas recargas seguintes.
    const [primeiraCargaCompleta, setPrimeiraCargaCompleta] = useState(false);
    const [acaoEmExecucao, setAcaoEmExecucao] = useState(null);

    const carregar = useCallback(() => {
        setLoading(true);
        solicitacaoService.buscarSolicitacao(id)
            .then(res => {
                const dados = res.data;
                setSolicitacao(dados);
                const processo = processoInfo(dados.tipoProcesso);

                return Promise.all([
                    solicitacaoService.buscarTimeline(id),
                    anexoService.listarAnexos(id),
                    processo?.temDetalhe ? processo.service.buscarDetalhe(id) : Promise.resolve({ data: null }),
                    solicitacaoService.buscarExecutoresDeAtividades(id),
                ]);
            })
            .then(([timelineRes, anexosRes, detalheRes, executoresRes]) => {
                setTimeline(timelineRes.data);
                setAnexos(anexosRes.data);
                setDetalheProcesso(detalheRes.data);
                setExecutores(executoresRes.data);
            })
            .catch(error => {
                console.error("Erro ao carregar solicitação", error);
            })
            .finally(() => {
                setLoading(false);
                setPrimeiraCargaCompleta(true);
            });
    }, [id]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    // Resolve com o SolicitacaoDTO já pós-transição (retornado pela própria chamada de ação) — quem
    // chama usa isso para popular a tela de confirmação e é responsável por chamar `recarregar()`
    // (não fazemos isso aqui para não disparar dois carregamentos quando o chamador também precisa
    // refletir o novo estado antes de decidir o que mostrar).
    function executarAcao(acao, extras = {}) {
        setAcaoEmExecucao(acao);
        return solicitacaoService.executarAcao(id, { acao, ...extras })
            .then(res => res.data)
            .catch(error => {
                console.error("Erro ao executar ação", error);
                throw error;
            })
            .finally(() => setAcaoEmExecucao(null));
    }

    function cancelarSolicitacao(motivo) {
        return solicitacaoService.cancelar(id, motivo)
            .then(res => res.data)
            .catch(error => {
                console.error("Erro ao cancelar solicitação", error);
                throw error;
            });
    }

    function assumirTarefa() {
        return solicitacaoService.assumirTarefa(id)
            .then(res => res.data)
            .catch(error => {
                console.error("Erro ao assumir tarefa", error);
                throw error;
            });
    }

    function transferirTarefa(novoUsuarioId, motivo) {
        return solicitacaoService.transferirTarefa(id, { novoUsuarioId, motivo })
            .then(res => res.data)
            .catch(error => {
                console.error("Erro ao transferir tarefa", error);
                throw error;
            });
    }

    function comentar(texto) {
        return solicitacaoService.comentar(id, { texto }).then(() => carregar());
    }

    function anexar(arquivo, descricao) {
        return anexoService.anexar(id, arquivo, descricao).then(() => carregar());
    }

    function excluirAnexo(anexoId) {
        return anexoService.excluirAnexo(anexoId).then(() => carregar());
    }

    // Assinatura de documento: troca o arquivo de um anexo já existente, sem criar um novo anexo.
    function substituirAnexo(anexoId, arquivo) {
        return anexoService.substituirArquivo(anexoId, arquivo).then(() => carregar());
    }

    return {
        solicitacao,
        detalheProcesso,
        timeline,
        anexos,
        loading,
        primeiraCargaCompleta,
        acaoEmExecucao,
        recarregar: carregar,
        executarAcao,
        cancelarSolicitacao,
        assumirTarefa,
        transferirTarefa,
        comentar,
        anexar,
        excluirAnexo,
        substituirAnexo,
        executores
    };
}
